import { Reveal } from "@/components/MaskedLine";
import { Axe, Flower2, TreeDeciduous, Check } from "lucide-react";

const chapters = [
  {
    num: "01",
    icon: Axe,
    title: "Élagage",
    subtitle: "La taille raisonnée, au service de l'arbre",
    img: "https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?w=1200&q=85&auto=format&fit=crop",
    clip: "clip-corner-tl",
    text: "Que ce soit pour la taille d'un bouleau centenaire ou la mise en sécurité d'un grand sujet, chaque coupe est réfléchie. Nous intervenons avec des techniques d'élagage respectueuses de la physiologie de l'arbre — taille douce, taille de formation, émondage — pour préserver sa santé et son esthétique durablement.",
    points: ["Taille douce & taille de formation", "Émondage et démontage sécurisé", "Abattage en espace contraint", "Explications détaillées à chaque étape"],
  },
  {
    num: "02",
    icon: Flower2,
    title: "Jardinage Paysager",
    subtitle: "Des jardins pensés comme des œuvres vivantes",
    img: "/images/chantier-allee.png",
    clip: "clip-corner-br",
    text: "De la création à l'entretien, nous dessinons et faisons vivre des jardins qui vous ressemblent. Tonte, taille de haies, massifs, plantations : un savoir-faire complet pour un extérieur impeccable en toute saison, avec un souci du détail qui fait la différence.",
    points: ["Création & aménagement paysager", "Taille de haies et topiaires", "Entretien de pelouses et massifs", "Plantations et végétalisation"],
  },
  {
    num: "03",
    icon: TreeDeciduous,
    title: "Arboriculture",
    subtitle: "L'expertise du vivant, du sol à la cime",
    img: "https://images.unsplash.com/photo-1598902108854-10e335adac99?w=1200&q=85&auto=format&fit=crop",
    clip: "clip-corner-tl",
    text: "Un arbre en bonne santé est un arbre sûr et beau. Notre regard d'arboriste nous permet de diagnostiquer l'état sanitaire de vos sujets, d'anticiper les risques et de préconiser les soins adaptés — pour que vos arbres traversent les générations.",
    points: ["Diagnostic sanitaire des arbres", "Étude de stabilité & gestion du risque", "Soins et traitements raisonnés", "Conseils de plantation d'essences"],
  },
];

export const ExpertiseManifesto = () => {
  return (
    <section id="expertises" data-testid="expertise-section" className="py-20 sm:py-28 lg:py-36 bg-[#EFECE4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-rb text-xs uppercase tracking-[0.25em] text-[#9E5A3C] mb-6">Le manifeste</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif-rb text-3xl sm:text-4xl lg:text-5xl text-[#17231A] max-w-3xl leading-tight" data-testid="expertise-title">
            Trois savoir-faire, <span className="italic text-[#2D5A3F]">une même exigence.</span>
          </h2>
        </Reveal>

        <div className="mt-20 space-y-24 lg:space-y-32">
          {chapters.map((c, i) => {
            const Icon = c.icon;
            const reversed = i % 2 === 1;
            return (
              <div
                key={c.num}
                data-testid={`chapter-${c.num}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center`}
              >
                <Reveal className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}>
                  <div className={`relative overflow-hidden ${c.clip} group`}>
                    <img
                      src={c.img}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-[340px] sm:h-[440px] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                    <span className="absolute bottom-5 left-5 font-mono-rb text-xs uppercase tracking-[0.25em] text-[#F5F3EE] bg-[#121F17]/60 backdrop-blur-sm rounded-full px-4 py-2">
                      Chapitre {c.num}
                    </span>
                  </div>
                </Reveal>
                <div className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
                  <Reveal delay={0.1}>
                    <div className="flex items-baseline gap-5">
                      <span className="font-serif-rb italic text-6xl lg:text-7xl text-[#2D5A3F]/15 leading-none">{c.num}</span>
                      <div>
                        <h3 className="font-serif-rb text-2xl sm:text-3xl lg:text-4xl text-[#17231A]">{c.title}</h3>
                        <p className="text-sm text-[#9E5A3C] italic mt-1 font-serif-rb">{c.subtitle}</p>
                      </div>
                    </div>
                  </Reveal>
                  <Reveal delay={0.2}>
                    <p className="mt-6 text-[#47594D] leading-relaxed text-base">{c.text}</p>
                  </Reveal>
                  <Reveal delay={0.3}>
                    <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {c.points.map((p) => (
                        <li key={p} className="flex items-start gap-2.5 text-sm text-[#17231A]">
                          <Check className="w-4 h-4 text-[#2D5A3F] mt-0.5 shrink-0" strokeWidth={2.5} />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                  <Reveal delay={0.4}>
                    <a
                      href="#contact"
                      data-testid={`chapter-cta-${c.num}`}
                      className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#2D5A3F] group"
                    >
                      <span className="border-b-2 border-[#2D5A3F]/30 pb-1 group-hover:border-[#2D5A3F] transition-colors duration-300">
                        Demander ce service
                      </span>
                      <Icon className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
                    </a>
                  </Reveal>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
